import React from 'react';
import { Link } from 'react-router-dom';
import { FileText, Map, Search, ShieldCheck, AlertTriangle, CheckCircle2, Clock, Home } from 'lucide-react';

/**
 * Ghid complet extras carte funciară (homepage) - conținut SEO informațional
 * Text static → ajunge direct în HTML-ul pre-randat.
 */
const H2 = ({ id, icon: Icon, children }) => (
  <h2 id={id} className="text-2xl md:text-3xl font-bold text-gray-900 mt-12 mb-4 flex items-start gap-3 scroll-mt-24">
    <Icon className="w-7 h-7 text-emerald-600 flex-shrink-0 mt-1" aria-hidden="true" />
    <span>{children}</span>
  </h2>
);

const HomeGuide = ({ onOrderClick }) => {
  return (
    <section id="ghid" className="py-16 bg-white">
      <div className="container mx-auto px-4">
        <article className="max-w-3xl mx-auto text-gray-700 leading-relaxed">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 text-center mb-4">
            Extras de carte funciară: ghid complet
          </h2>
          <p className="text-center text-gray-500 mb-10">
            Tot ce trebuie să știi înainte să comanzi: ce este, ce conține, cum îl obții și la ce îl folosești.
          </p>

          {/* Cuprins */}
          <nav aria-label="Cuprins ghid" className="bg-emerald-50 border border-emerald-200 rounded-xl p-6 mb-10">
            <p className="font-semibold text-gray-900 mb-3">Pe scurt, în acest ghid:</p>
            <ol className="list-decimal list-inside space-y-1 text-emerald-800">
              <li><a href="#ce-este" className="hover:underline">Ce este extrasul de carte funciară</a></li>
              <li><a href="#ce-contine" className="hover:underline">Ce conține extrasul (partea I, II și III)</a></li>
              <li><a href="#cf-vs-plan" className="hover:underline">Extras CF sau plan cadastral: pe care îl alegi</a></li>
              <li><a href="#numar-cadastral" className="hover:underline">Cum găsești numărul cadastral</a></li>
              <li><a href="#cum-comanzi" className="hover:underline">Cum comanzi extrasul online, pas cu pas</a></li>
              <li><a href="#cand-ai-nevoie" className="hover:underline">Când ai nevoie de extras de carte funciară</a></li>
              <li><a href="#valabilitate" className="hover:underline">Cât este valabil un extras CF</a></li>
              <li><a href="#greseli" className="hover:underline">Greșeli frecvente și cum le eviți</a></li>
            </ol>
          </nav>

          <H2 id="ce-este" icon={FileText}>Ce este extrasul de carte funciară</H2>
          <p className="mb-4">
            <strong>Cartea funciară</strong> este registrul oficial în care este înscrisă situația juridică a unui imobil:
            cine este proprietarul, ce suprafață are, ce construcții există pe el și dacă este grevat de ipoteci,
            sechestre sau alte sarcini. Fiecare imobil înscris are propria carte funciară, identificată printr-un număr
            și prin localitatea (UAT-ul) în care se află.
          </p>
          <p className="mb-4">
            <strong>Extrasul de carte funciară</strong> este documentul care redă conținutul acestui registru la un
            anumit moment. Practic, este „fotografia" juridică a imobilului la data și ora emiterii. Prescurtarea
            uzuală este <strong>extras CF</strong>.
          </p>
          <p className="mb-4">
            Există două tipuri de extrase, cu roluri diferite:
          </p>
          <ul className="list-disc pl-6 space-y-2 mb-4">
            <li>
              <strong>Extrasul de carte funciară pentru informare</strong> - îl poate cere oricine, pentru orice imobil
              înscris, fiindcă cartea funciară este publică. Este documentul pe care îl primești de la noi și pe care îl
              folosești pentru verificări, la bancă, la notar pentru informare, în instanță sau în relația cu primăria.
            </li>
            <li>
              <strong>Extrasul de carte funciară pentru autentificare</strong> - îl cere direct notarul public atunci
              când autentifică un act de vânzare sau o ipotecă. Acest extras „blochează" cartea funciară pentru 10 zile
              lucrătoare, ca să nu se poată înscrie altceva până la semnarea actului. Nu îl comanzi tu; se ocupă notarul.
            </li>
          </ul>
          <p>
            Pe scurt: dacă vrei să afli situația unui imobil, ai nevoie de extrasul pentru informare. Îl primești în
            format PDF, semnat electronic, și îl poți printa sau trimite mai departe pe email.
          </p>

          <H2 id="ce-contine" icon={Search}>Ce conține extrasul de carte funciară</H2>
          <p className="mb-4">
            Orice extras CF are aceeași structură, împărțită în trei părți. Dacă știi ce să cauți în fiecare, citești
            documentul în două minute.
          </p>
          <h3 className="text-xl font-semibold text-gray-900 mt-6 mb-2">Partea I - descrierea imobilului</h3>
          <p className="mb-4">
            Aici găsești numărul cadastral, suprafața din acte și cea măsurată, adresa, categoria de folosință (de
            exemplu curți-construcții, arabil, pășune) și construcțiile înscrise, cu numerele lor (C1, C2 etc.). Pentru
            apartamente apare și cota-parte din părțile comune ale blocului.
          </p>
          <h3 className="text-xl font-semibold text-gray-900 mt-6 mb-2">Partea a II-a - proprietarii</h3>
          <p className="mb-4">
            Arată cine deține imobilul, în ce cote (de exemplu 1/2 fiecare soț) și pe ce bază a fost dobândit dreptul:
            contract de vânzare-cumpărare, certificat de moștenitor, donație, hotărâre judecătorească. Aceasta este
            partea pe care o verifici prima atunci când vrei să știi dacă vânzătorul este cu adevărat proprietarul.
          </p>
          <h3 className="text-xl font-semibold text-gray-900 mt-6 mb-2">Partea a III-a - sarcinile</h3>
          <p className="mb-4">
            Este partea cea mai importantă pentru siguranța ta. Aici apar ipotecile băncilor, sechestrele, interdicțiile
            de înstrăinare și grevare, servituțile (de exemplu un drept de trecere) și litigiile notate. Dacă această
            parte este goală, imobilul nu are sarcini înscrise. Dacă apare o ipotecă, înseamnă că imobilul garantează un
            credit, iar vânzarea se face de obicei cu acordul și prin intermediul băncii.
          </p>
          <div className="bg-amber-50 border-l-4 border-amber-400 rounded-r-lg p-4 my-6">
            <p className="flex items-start gap-2 text-amber-900">
              <AlertTriangle className="w-5 h-5 flex-shrink-0 mt-0.5" aria-hidden="true" />
              <span>
                <strong>Sfat:</strong> nu plăti niciodată un avans pentru un imobil fără să vezi un extras de carte
                funciară emis recent. Durează câteva minute și te poate scuti de surprize foarte scumpe.
              </span>
            </p>
          </div>

          <H2 id="cf-vs-plan" icon={Map}>Extras CF sau extras de plan cadastral: pe care îl alegi</H2>
          <p className="mb-4">
            Pe CF-Extras.ro poți comanda două documente. Alegerea depinde de ce vrei să afli:
          </p>
          <div className="overflow-x-auto mb-4">
            <table className="w-full text-sm border border-gray-200 rounded-lg">
              <thead className="bg-gray-50 text-gray-900">
                <tr>
                  <th className="text-left p-3 border-b"> </th>
                  <th className="text-left p-3 border-b">Extras de carte funciară</th>
                  <th className="text-left p-3 border-b">Extras de plan cadastral</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td className="p-3 border-b font-medium text-gray-900">Ce arată</td>
                  <td className="p-3 border-b">Proprietari, suprafață, construcții, ipoteci și sarcini</td>
                  <td className="p-3 border-b">Imobilul pe hartă, peste fotografia aeriană (ortofotoplan)</td>
                </tr>
                <tr>
                  <td className="p-3 border-b font-medium text-gray-900">Pentru ce</td>
                  <td className="p-3 border-b">Terenuri, case, apartamente</td>
                  <td className="p-3 border-b">Mai ales terenuri</td>
                </tr>
                <tr>
                  <td className="p-3 font-medium text-gray-900">Când îl folosești</td>
                  <td className="p-3">Vânzare, cumpărare, credit, moștenire, verificări juridice</td>
                  <td className="p-3">Verifici amplasamentul, forma, vecinii și accesul la teren</td>
                </tr>
              </tbody>
            </table>
          </div>
          <p>
            Dacă vrei să cumperi un teren, cele două documente se completează: extrasul CF îți spune <em>al cui</em> este
            terenul și dacă are probleme juridice, iar planul cadastral îți arată <em>unde</em> este exact și cum arată.
            Pentru un apartament, extrasul de carte funciară este de regulă suficient.
          </p>

          <H2 id="numar-cadastral" icon={Home}>Cum găsești numărul cadastral sau numărul de carte funciară</H2>
          <p className="mb-4">
            Pentru comandă ai nevoie de județ, localitate și de numărul imobilului. Numărul îl găsești, de obicei, în:
          </p>
          <ul className="list-disc pl-6 space-y-2 mb-4">
            <li>contractul de vânzare-cumpărare, de donație sau certificatul de moștenitor;</li>
            <li>un extras de carte funciară mai vechi, primit de la notar sau de la bancă;</li>
            <li>documentația cadastrală întocmită de inginerul topograf;</li>
            <li>contractul de credit ipotecar, unde imobilul adus în garanție este identificat complet.</li>
          </ul>
          <p className="mb-4">
            În majoritatea cazurilor, la imobilele înscrise în sistemul electronic, <strong>numărul cărții funciare este
            același cu numărul cadastral</strong> (de exemplu 203242). Pentru apartamente, identificatorul are forma
            <strong> 12345-C1-U3</strong>: numărul terenului, construcția (C1) și unitatea individuală (U3). Introdu-l
            exact așa, cu cratimele.
          </p>
          <p>
            Ce <strong>nu</strong> introduci în câmp: cuvinte precum „CF nr." sau „cadastral", numele străzii ori
            numărul de apartament din adresă. Formularul nostru verifică formatul și îți spune imediat dacă ceva nu e în
            regulă, înainte de plată.
          </p>

          <H2 id="cum-comanzi" icon={CheckCircle2}>Cum comanzi extrasul de carte funciară online, pas cu pas</H2>
          <ol className="list-decimal pl-6 space-y-3 mb-4">
            <li>
              <strong>Introduci datele imobilului</strong> - județul, localitatea și numărul cadastral sau de carte
              funciară. Formularul verifică formatul numărului înainte să mergi mai departe.
            </li>
            <li>
              <strong>Alegi documentul</strong> - extras de carte funciară sau extras de plan cadastral.
            </li>
            <li>
              <strong>Completezi datele de facturare și emailul</strong> - pe persoană fizică sau pe firmă. Nu ai nevoie
              de cont.
            </li>
            <li>
              <strong>Plătești cu cardul</strong> - prin Stripe, în siguranță. Prețul este fix și include taxa de
              eliberare.
            </li>
            <li>
              <strong>Primești documentul pe email</strong> - în aproximativ 5 minute, în format PDF, împreună cu factura.
            </li>
          </ol>
          <p className="mb-6">
            Nu trebuie să mergi la vreun ghișeu, nu stai la coadă și nu completezi cereri pe hârtie. Poți comanda la
            orice oră, de pe telefon sau de pe calculator.
          </p>
          <div className="text-center mb-2">
            <button
              type="button"
              onClick={onOrderClick}
              className="inline-flex items-center gap-2 px-8 py-4 bg-emerald-600 text-white font-semibold rounded-xl hover:bg-emerald-700 transition shadow-lg"
            >
              <FileText className="w-5 h-5" aria-hidden="true" />
              Comandă extrasul acum
            </button>
          </div>

          <H2 id="cand-ai-nevoie" icon={ShieldCheck}>Când ai nevoie de extras de carte funciară</H2>
          <p className="mb-4">Cele mai frecvente situații în care clienții noștri comandă un extras CF:</p>
          <ul className="list-disc pl-6 space-y-2 mb-4">
            <li>
              <strong>Înainte să cumperi un imobil</strong> - verifici că vânzătorul este proprietarul și că nu există
              ipoteci, sechestre sau litigii. Este cel mai important moment pentru un extras.
            </li>
            <li>
              <strong>Când vinzi</strong> - îi arăți cumpărătorului situația clară a imobilului și grăbești negocierea.
            </li>
            <li>
              <strong>Pentru un credit ipotecar sau o refinanțare</strong> - banca cere extrasul imobilului adus în
              garanție.
            </li>
            <li>
              <strong>La succesiune</strong> - afli ce imobile sunt înscrise pe numele persoanei decedate și în ce cote.
            </li>
            <li>
              <strong>La închiriere</strong> - verifici că persoana care îți închiriază locuința are dreptul să o facă.
            </li>
            <li>
              <strong>În instanță, la primărie sau la autorizarea unei construcții</strong> - ca dovadă a situației
              juridice a imobilului.
            </li>
            <li>
              <strong>Pentru firme și profesioniști</strong> - agenți imobiliari, avocați, executori, evaluatori și
              dezvoltatori care verifică zilnic imobile.
            </li>
          </ul>

          <H2 id="valabilitate" icon={Clock}>Cât este valabil un extras de carte funciară</H2>
          <p className="mb-4">
            Extrasul de carte funciară pentru informare <strong>nu are un termen legal de valabilitate</strong>. El
            arată situația imobilului la data și ora la care a fost emis, iar această informație poate deveni depășită
            oricând se înscrie ceva nou: o vânzare, o ipotecă, un sechestru.
          </p>
          <p className="mb-4">
            De aceea, în practică, băncile, notarii și alte instituții cer un extras <strong>emis recent</strong>, de
            regulă din ultimele 30 de zile, iar pentru o tranzacție imobiliară e bine să verifici situația chiar în
            săptămâna semnării. Înainte să depui documentul, întreabă instituția ce vechime acceptă.
          </p>
          <p>
            Extrasul pentru autentificare, cel cerut de notar, este altceva: el este valabil 10 zile lucrătoare, timp în
            care cartea funciară rămâne blocată pentru alte înscrieri.
          </p>

          <H2 id="greseli" icon={AlertTriangle}>Greșeli frecvente și cum le eviți</H2>
          <ul className="list-disc pl-6 space-y-3 mb-6">
            <li>
              <strong>Alegerea unei alte localități.</strong> Același număr cadastral poate exista în mai multe UAT-uri.
              Verifică de două ori județul și localitatea în care se află imobilul, nu domiciliul tău.
            </li>
            <li>
              <strong>Numărul de apartament scris greșit.</strong> Pentru apartamente folosește identificatorul complet
              (de exemplu 12345-C1-U3), nu doar numărul blocului.
            </li>
            <li>
              <strong>Carte funciară închisă (sistată).</strong> Când un teren se dezmembrează, se alipește sau un bloc
              se împarte pe apartamente, cartea funciară veche se închide și imobilul primește un număr nou. Dacă
              numărul tău este al unei cărți închise, scrie-ne și te ajutăm să obții extrasul pentru numărul nou.
            </li>
            <li>
              <strong>Imobil neînscris în cartea funciară.</strong> Dacă imobilul nu a fost niciodată intabulat, nu
              există carte funciară și nici extras. În acest caz ai nevoie mai întâi de o documentație cadastrală.
            </li>
            <li>
              <strong>Un extras prea vechi.</strong> Pentru tranzacții nu te baza pe un extras de acum câteva luni.
              Comandă unul nou, chiar înainte de semnare.
            </li>
          </ul>
          <p>
            Comanzi pentru orice localitate din România. Vezi și paginile dedicate pentru{' '}
            <Link to="/extras-carte-funciara/bucuresti/" className="text-emerald-700 hover:underline">București</Link>,{' '}
            <Link to="/extras-carte-funciara/cluj-napoca/" className="text-emerald-700 hover:underline">Cluj-Napoca</Link>,{' '}
            <Link to="/extras-carte-funciara/timisoara/" className="text-emerald-700 hover:underline">Timișoara</Link>,{' '}
            <Link to="/extras-carte-funciara/iasi/" className="text-emerald-700 hover:underline">Iași</Link> sau{' '}
            <Link to="/extras-carte-funciara/" className="text-emerald-700 hover:underline">lista completă de orașe</Link>.
          </p>
          <p className="mt-4">
            Vrei să afli mai multe? Citește ghidurile noastre:{' '}
            <Link to="/ghid/cum-citesti-extras-carte-funciara/" className="text-emerald-700 hover:underline">cum citești un extras CF</Link>,{' '}
            <Link to="/ghid/valabilitate-extras-carte-funciara/" className="text-emerald-700 hover:underline">cât este valabil un extras</Link>,{' '}
            <Link to="/ghid/cum-afli-numarul-cadastral/" className="text-emerald-700 hover:underline">cum afli numărul cadastral</Link> și{' '}
            <Link to="/ghid/verificari-inainte-de-cumparare-imobil/" className="text-emerald-700 hover:underline">ce verifici înainte să cumperi un imobil</Link>.
          </p>
        </article>
      </div>
    </section>
  );
};

export default HomeGuide;
